import { useMemo } from "react";
import { useParams } from "react-router";

/**
 * The literal segment that introduces an agent scope, shared by the route table and
 * {@link scopeSegment} so a rename cannot leave one of them behind.
 */
export const INBOX_AGENT_SEGMENT = "agents";

/** The same, for an agency scope. */
export const INBOX_AGENCY_SEGMENT = "agencies";

/**
 * What the inbox route is narrowed to, if anything.
 *
 * The three kinds are mutually exclusive by construction rather than by a tie-break:
 * `agencies/:agencyId`, `agents/:agentId` and `:insuredId?` are sibling routes in
 * `mini-app-routes.tsx`, so one pathname cannot match two of them, and navigating to any of them
 * from a page scoped to another replaces the segment outright. The intersection of two scopes is a
 * separate shape.
 *
 * Carries only the id, with everything derivable from it left to the selectors below — there is no
 * way to hold a scope whose path disagrees with its id.
 */
export type InboxScope =
  | { kind: "agency"; agencyId: string }
  | { kind: "agent"; agentId: string }
  | { kind: "insured"; insuredId: string };

/**
 * The pathname parts that carry the scope. Two for an agent or an agency and one for an insured,
 * which is the whole reason this is a function and not a segment count: composing a scoped path and
 * releasing one are both string operations over this value, so neither has to know the shape of any
 * kind.
 */
export const scopeSegment = (scope: InboxScope): string => {
  switch (scope.kind) {
    case "agency":
      return `${INBOX_AGENCY_SEGMENT}/${scope.agencyId}`;
    case "agent":
      return `${INBOX_AGENT_SEGMENT}/${scope.agentId}`;
    case "insured":
      return scope.insuredId;
  }
};

/** The insured narrowing the route, and nothing under any other kind of scope. */
export const scopedInsuredId = (scope: InboxScope | null): string | undefined =>
  scope?.kind === "insured" ? scope.insuredId : undefined;

/** The agent narrowing the route, and nothing under any other kind of scope. */
export const scopedAgentId = (scope: InboxScope | null): string | undefined =>
  scope?.kind === "agent" ? scope.agentId : undefined;

/** The agency narrowing the route, and nothing under any other kind of scope. */
export const scopedAgencyId = (scope: InboxScope | null): string | undefined =>
  scope?.kind === "agency" ? scope.agencyId : undefined;

/**
 * The route's scope, derived in one place so the chip, the folder rows, the Book rail and the
 * conversation filters cannot disagree about what the list is narrowed to.
 *
 * `useParams` reaches the deepest match from anywhere above it, which is what lets the providers
 * above `/inbox` read a param declared several routes down.
 *
 * The `files` exclusion comes from the insured scope and is belt-and-braces now that `files` is a
 * route ahead of the param — but the param is what the app reads, and `:insuredId?` does carry a
 * `files` child, so it stays. Neither set scope needs a counterpart for the same reason each needs
 * its bare route: `agents` and `agencies` match literally in both forms, so `insuredId` can never
 * become either string in the several places that read it raw.
 *
 * Memoized so consumers can key a `useMemo` on the whole scope rather than on its pieces.
 */
export const useInboxScope = (): InboxScope | null => {
  const { agencyId, agentId, insuredId } = useParams();

  return useMemo(() => {
    if (agencyId) {
      return { kind: "agency", agencyId };
    }

    if (agentId) {
      return { kind: "agent", agentId };
    }

    if (insuredId && insuredId !== "files") {
      return { kind: "insured", insuredId };
    }

    return null;
  }, [agencyId, agentId, insuredId]);
};
